import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { X } from 'lucide-react';
import { PromoCode, DiscountType } from '../../model/professional/promo-code-model';
import serviceStore from '../../store/service-store';

interface PromoCodeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PromoCodeFormData) => void;
  companyId: string;
  promoCode?: PromoCode | null;
}

export interface PromoCodeFormData {
  code: string;
  discountType: DiscountType;
  discountValue: number;
  validFrom: string;
  validUntil?: string;
  applicableServiceIds?: string[];
  assignedClientIds?: string[];
  description?: string;
  internalNotes?: string;
}

const PromoCodeFormModal: React.FC<PromoCodeFormModalProps> = observer(({
  isOpen,
  onClose,
  onSubmit,
  companyId,
  promoCode,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<PromoCodeFormData>({
    code: '',
    discountType: 'percentage',
    discountValue: 0,
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: undefined,
    applicableServiceIds: [],
    assignedClientIds: [],
    description: '',
    internalNotes: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof PromoCodeFormData, string>>>({});

  // Pré-remplir le formulaire en mode édition
  useEffect(() => {
    if (promoCode) {
      setFormData({
        code: promoCode.code,
        discountType: promoCode.discountType,
        discountValue: promoCode.discountValue,
        validFrom: new Date(promoCode.validFrom).toISOString().split('T')[0],
        validUntil: promoCode.validUntil ? new Date(promoCode.validUntil).toISOString().split('T')[0] : undefined,
        applicableServiceIds: promoCode.applicableServiceIds || [],
        assignedClientIds: promoCode.assignedClientIds || [],
        description: promoCode.description || '',
        internalNotes: promoCode.internalNotes || '',
      });
    } else {
      // Réinitialiser le formulaire en mode création
      setFormData({
        code: '',
        discountType: 'percentage',
        discountValue: 0,
        validFrom: new Date().toISOString().split('T')[0],
        validUntil: undefined,
        applicableServiceIds: [],
        assignedClientIds: [],
        description: '',
        internalNotes: '',
      });
    }
    setErrors({});
  }, [promoCode, isOpen]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof PromoCodeFormData, string>> = {};

    if (!formData.code.trim()) {
      newErrors.code = String(t('promoCode.error.codeRequired', 'Le code est requis'));
    } else if (formData.code.length < 3) {
      newErrors.code = String(t('promoCode.error.codeTooShort', 'Le code doit contenir au moins 3 caractères'));
    }

    if (formData.discountValue <= 0) {
      newErrors.discountValue = String(t('promoCode.error.discountValuePositive', 'La valeur doit être positive'));
    }

    if (formData.discountType === 'percentage' && formData.discountValue > 100) {
      newErrors.discountValue = String(t('promoCode.error.percentageMax100', 'Le pourcentage ne peut pas dépasser 100%'));
    }

    if (!formData.validFrom) {
      newErrors.validFrom = String(t('promoCode.error.validFromRequired', 'La date de début est requise'));
    }

    if (formData.validUntil && formData.validFrom && new Date(formData.validUntil) < new Date(formData.validFrom)) {
      newErrors.validUntil = String(t('promoCode.error.validUntilAfterValidFrom', 'La date de fin doit être après la date de début'));
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof PromoCodeFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Effacer l'erreur du champ modifié
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleServiceToggle = (serviceId: string) => {
    setFormData(prev => ({
      ...prev,
      applicableServiceIds: prev.applicableServiceIds?.includes(serviceId)
        ? prev.applicableServiceIds.filter(id => id !== serviceId)
        : [...(prev.applicableServiceIds || []), serviceId],
    }));
  };

  const companyServices = serviceStore.services.filter(s => s.companyId === companyId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {promoCode
              ? t('promoCode.edit', 'Modifier le code promo')
              : t('promoCode.create', 'Créer un code promo')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('promoCode.form.code', 'Code promo')} *
            </label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
              className={`w-full px-4 py-2 border rounded-lg font-mono ${
                errors.code
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
              placeholder="PROMO2025"
            />
            {errors.code && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.code}</p>}
          </div>

          {/* Type de réduction */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('promoCode.form.discountType', 'Type de réduction')} *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="percentage"
                  checked={formData.discountType === 'percentage'}
                  onChange={(e) => handleChange('discountType', e.target.value as DiscountType)}
                  className="mr-2"
                />
                <span className="text-gray-700 dark:text-gray-300">
                  {t('promoCode.form.percentage', 'Pourcentage')}
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="fixed_amount"
                  checked={formData.discountType === 'fixed_amount'}
                  onChange={(e) => handleChange('discountType', e.target.value as DiscountType)}
                  className="mr-2"
                />
                <span className="text-gray-700 dark:text-gray-300">
                  {t('promoCode.form.fixed', 'Montant fixe')}
                </span>
              </label>
            </div>
          </div>

          {/* Valeur de réduction */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('promoCode.form.discountValue', 'Valeur de la réduction')} *
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max={formData.discountType === 'percentage' ? 100 : undefined}
                step={formData.discountType === 'percentage' ? 1 : 0.01}
                value={formData.discountValue}
                onChange={(e) => handleChange('discountValue', parseFloat(e.target.value))}
                className={`flex-1 px-4 py-2 border rounded-lg ${
                  errors.discountValue
                    ? 'border-red-500 dark:border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
              />
              <span className="text-gray-700 dark:text-gray-300">
                {formData.discountType === 'percentage' ? '%' : '€'}
              </span>
            </div>
            {errors.discountValue && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.discountValue}</p>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('promoCode.form.validFrom', 'Valide à partir du')} *
              </label>
              <input
                type="date"
                value={formData.validFrom}
                onChange={(e) => handleChange('validFrom', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${
                  errors.validFrom
                    ? 'border-red-500 dark:border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
              />
              {errors.validFrom && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.validFrom}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('promoCode.form.validUntil', "Valide jusqu'au")}
              </label>
              <input
                type="date"
                value={formData.validUntil || ''}
                onChange={(e) => handleChange('validUntil', e.target.value || undefined)}
                className={`w-full px-4 py-2 border rounded-lg ${
                  errors.validUntil
                    ? 'border-red-500 dark:border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
              />
              {errors.validUntil && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.validUntil}</p>
              )}
            </div>
          </div>

          {/* Services applicables */}
          {companyServices.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('promoCode.form.applicableServices', 'Services applicables')}
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {t('promoCode.form.applicableServicesHelp', 'Laissez vide pour appliquer à tous les services')}
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                {companyServices.map(service => (
                  <label key={service.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.applicableServiceIds?.includes(service.id) || false}
                      onChange={() => handleServiceToggle(service.id)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{service.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('promoCode.form.description', 'Description publique')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows={3}
              placeholder={String(t('promoCode.form.descriptionPlaceholder', 'Description visible par les clients'))}
            />
          </div>

          {/* Notes internes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('promoCode.form.internalNotes', 'Notes internes')}
            </label>
            <textarea
              value={formData.internalNotes}
              onChange={(e) => handleChange('internalNotes', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows={2}
              placeholder={String(t('promoCode.form.internalNotesPlaceholder', 'Notes visibles uniquement par vous'))}
            />
          </div>

          {/* Boutons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('common.cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              {promoCode
                ? t('common.save', 'Enregistrer')
                : t('common.create', 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default PromoCodeFormModal;
